// Navbar.jsx - Updated for Unified Admin Dashboard
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth.jsx";
import { 
  Eye, 
  LayoutDashboard, 
  Search,
  BarChart3, 
  Globe,
  User, 
  Settings, 
  LogOut,
  Shield,
  Compass,
  Zap,
  Hexagon,
  Layers3,
  Radar,
  Crown,
  Terminal
} from 'lucide-react';

export default function Navbar() {
  const { user, logout, getCurrentTier } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [blinkingEye, setBlinkingEye] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  // Get user tier safely
  let currentTier = 'free';
  try {
    currentTier = getCurrentTier ? getCurrentTier() : 'free';
  } catch (error) {
    console.warn('Error getting tier:', error);
  }

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.tier === 'admin' || user?.email === 'admin@coderated.com';

  // Blinking eye effect
  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkingEye((prev) => !prev);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Scroll behavior for auto-hide navbar
  useEffect(() => {
    const handleScroll = () => {
      if (isLocked) return; // Don't hide if locked
      
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    const throttledScroll = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [lastScrollY, isLocked]);

  // Show navbar on mouse move to top
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (e.clientY < 100 && !isVisible && !isLocked) {
        setIsVisible(true);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isVisible, isLocked]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Only show navbar for authenticated users
  if (!user) {
    return null;
  }

  const handleSignOut = () => {
    setIsMenuOpen(false);
    logout();
    navigate("/landing");
  };

  // Updated Navigation items with unified admin dashboard
  const navigationItems = [
    { 
      path: "/dashboard", 
      label: "Command Center", 
      description: "Central Intelligence Hub",
      icon: Compass,
      iconClass: "animate-pulse"
    },
    { 
      path: "/feed", 
      label: "Discovery Feed", 
      description: "Global Intelligence Network", 
      icon: Radar,
      iconClass: "animate-spin-slow"
    },
    {
      path: "/show",
      label: "Tune In",
      description: "Code Rating Code",
      icon: Eye,
      iconClass: "animate-bounce-subtle",
    },
    
    // UPDATED: Single unified admin dashboard
    ...(isAdmin ? [{
      path: "/admin-dashboard", 
      label: "Admin Control", 
      description: "System Management & AI Training",
      icon: Terminal, // More professional terminal icon
      isAdmin: true,
      iconClass: "animate-pulse"
    }] : []),
    
    { 
      path: "/analytics", 
      label: "Analytics", 
      description: "Deep Performance Insights", 
      icon: BarChart3,
      iconClass: "animate-pulse-slow"
    },
  ];

  const isActivePath = (path) => {
    if (path === "/dashboard") return location.pathname === "/" || location.pathname === "/dashboard";
    if (path === "/admin-dashboard") return location.pathname.startsWith("/admin");
    return location.pathname.startsWith(path);
  };

  // Cute placeholder business avatar (cake store)
  const BusinessAvatar = () => (
    <div className="w-10 h-10 bg-gradient-to-br from-pink-400 via-rose-300 to-orange-300 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/20 hover:ring-white/40 transition-all duration-300 group-hover:scale-110">
      <div className="text-white text-lg font-bold">🧁</div>
    </div>
  );

  return (
    <>
      {/* Main Navbar with enhanced transparency and animations */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      } ${
        isLocked ? 'bg-slate-900/95' : 'bg-transcluent backdrop-blur-lg'
      } backdrop-none-xl border-b border-slate-700/30`}>
        
        {/* Gradient overlay for extra depth */}
        <div className="transclucent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* Your Original Logo - UNCHANGED */}
            <Link to="/dashboard" className="group flex items-center gap-3 p-2 hover:scale-110 transition-all duration-300">
              <div className="relative">
                {/* Blue glow effect on hover */}
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                
                <div className="relative w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-500 rounded-lg border-2 border-gray-600 shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300">
                  <div className="w-8 h-5 bg-black mx-auto mt-1 rounded border border-gray-800 flex items-center justify-center">
                    <div className={`transition-all duration-150 ${blinkingEye ? 'scale-100' : 'scale-y-10'}`}>
                      <Eye className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300" />
                    </div>
                  </div>
                  <div className="flex justify-center mt-0.5 gap-1">
                    <div className="w-1 h-0.5 bg-red-500 rounded group-hover:bg-red-400 transition-colors duration-300"></div>
                    <div className="w-1 h-0.5 bg-green-500 rounded group-hover:bg-green-400 transition-colors duration-300"></div>
                  </div>
                </div>
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-2 bg-gray-600 rounded-b-lg group-hover:bg-gray-500 transition-colors duration-300"></div>
              </div>
              <span className="text-xl font-bold text-white hidden sm:block">CodeRated</span>
            </Link>

            {/* Center Navigation - Enhanced with unique icons */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = isActivePath(item.path);
                
                return (
                  <div key={item.path} className="relative group">
                    <Link
                      to={item.path}
                      className={`flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300 ${
                        isActive
                          ? item.isAdmin 
                            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg shadow-red-500/25 scale-110' 
                            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25 scale-110'
                          : 'text-slate-300 hover:text-white hover:bg-white/10 hover:scale-105 backdrop-blur-sm'
                      } ${item.iconClass || ''}`}
                      style={{ pointerEvents: 'auto', zIndex: 1001 }}
                    >
                    <IconComponent className="w-5 h-5" />
                    </Link>

                    {/* Enhanced Hover Tooltip */}
                    <div className="absolute top-full mt-3 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
                      <div className="relative">
                        {/* Enhanced glow effect */}
                        <div className={`absolute -inset-3 ${
                          item.isAdmin 
                            ? 'bg-gradient-to-r from-red-400/30 via-orange-400/30 to-pink-400/30' 
                            : 'bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-cyan-400/30'
                        } rounded-2xl blur-lg`}></div>
                        
                        <div className={`relative bg-slate-900/95 backdrop-blur-xl text-white text-sm rounded-2xl px-5 py-4 whitespace-nowrap ${
                          item.isAdmin 
                            ? 'border border-red-400/40 shadow-2xl shadow-red-500/20' 
                            : 'border border-blue-400/40 shadow-2xl shadow-blue-500/20'
                        }`}>
                          <div className={`font-bold text-base mb-1 ${item.isAdmin ? 'text-red-100' : 'text-blue-100'}`}>
                            {item.label}
                            {item.isAdmin && (
                              <span className="ml-2 text-xs bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-full">
                                ADMIN
                              </span>
                            )}
                          </div>
                          <div className="text-slate-300 text-xs">{item.description}</div>
                          
                          {/* Enhanced tooltip arrow */}
                          <div className={`absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-slate-900/95 rotate-45 ${
                            item.isAdmin 
                              ? 'border-l border-t border-red-400/40' 
                              : 'border-l border-t border-blue-400/40'
                          }`}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Side - Profile & Controls */}
            <div className="flex items-center gap-4">
              {/* Navbar Lock Toggle */}
              <button
                onClick={() => setIsLocked(!isLocked)}
                className={`hidden md:flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 ${
                  isLocked 
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                    : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
                title={isLocked ? 'Unlock navbar' : 'Lock navbar in place'}
              >
                <Zap className={`w-4 h-4 ${isLocked ? 'animate-pulse' : ''}`} />
              </button>

              {/* User Tier Badge */}
              <div className={`hidden sm:flex px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-sm ${
                isAdmin
                  ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-red-200 border border-red-400/30'
                  : currentTier === 'business' 
                  ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-200 border border-emerald-400/30'
                  : currentTier === 'professional'
                  ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-200 border border-blue-400/30'
                  : 'bg-gradient-to-r from-slate-500/20 to-gray-500/20 text-slate-200 border border-slate-400/30'
              } shadow-lg`}>
                {isAdmin ? (
                  <div className="flex items-center space-x-1">
                    <Crown className="w-3 h-3" />
                    <span>Admin</span>
                  </div>
                ) : (
                  currentTier.charAt(0).toUpperCase() + currentTier.slice(1)
                )}
              </div>

              {/* Profile Picture */}
              <div className="relative group">
                <Link
                  to="/profile"
                  className="block transition-all duration-300 hover:scale-110"
                  style={{ pointerEvents: 'auto', zIndex: 1001 }}
                >
                  <BusinessAvatar />
                </Link>
                
                {/* Profile tooltip */}
                <div className="absolute top-full mt-3 right-0 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
                  <div className="relative">
                    <div className="absolute -inset-2 bg-gradient-to-r from-pink-400/20 via-rose-400/20 to-orange-400/20 rounded-xl blur-md"></div>
                    
                    <div className="relative bg-slate-900/95 backdrop-blur-xl text-white text-sm rounded-xl px-4 py-3 whitespace-nowrap border border-pink-400/30 shadow-2xl shadow-pink-500/10">
                      <div className="font-semibold text-pink-100 flex items-center">
                        <span className="mr-2">🧁</span>
                        Sweet Treats Bakery
                      </div>
                      <div className="text-slate-300 text-xs mt-1">Click to manage profile</div>
                      
                      <div className="absolute -top-2 right-4 w-4 h-4 bg-slate-900/95 rotate-45 border-l border-t border-pink-400/30"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Sign Out with enhanced styling */}
              <div className="hidden md:block relative group">
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-300 hover:text-white hover:bg-red-500/20 transition-all duration-200 hover:scale-105"
                  style={{ pointerEvents: 'auto', zIndex: 1001 }}
                >
                  <LogOut className="w-4 h-4" />
                </button>
                
                {/* Enhanced sign out tooltip */}
                <div className="absolute top-full mt-3 right-0 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
                  <div className="relative">
                    <div className="absolute -inset-2 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-xl blur-md"></div>
                    
                    <div className="relative bg-slate-900/95 backdrop-blur-xl text-white text-sm rounded-xl px-4 py-3 whitespace-nowrap border border-red-400/30 shadow-2xl shadow-red-500/10">
                      <div className="font-semibold text-red-100">Sign Out</div>
                      <div className="text-slate-300 text-xs mt-1">End session securely</div>
                      
                      <div className="absolute -top-2 right-4 w-4 h-4 bg-slate-900/95 rotate-45 border-l border-t border-red-400/30"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Menu Button - Enhanced */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200 hover:scale-105"
                style={{ pointerEvents: 'auto', zIndex: 1001 }}
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${
                    isMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                  }`}></span>
                  <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${
                    isMenuOpen ? 'opacity-0' : ''
                  }`}></span>
                  <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${
                    isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                  }`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Enhanced Mobile Menu */}
      {isMenuOpen && (
        <>
          {/* Enhanced Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
            onClick={() => setIsMenuOpen(false)}
            style={{ pointerEvents: 'auto' }}
          />
          
          {/* Enhanced Menu Panel */}
          <div className="fixed top-16 left-0 right-0 z-50 md:hidden" style={{ pointerEvents: 'auto' }}>
            <div className="bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 shadow-2xl">
              <div className="max-w-7xl mx-auto px-4 py-6 space-y-3">
                
                {/* Enhanced User Info */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-xl mb-6 border border-slate-600/30">
                  <BusinessAvatar />
                  <div>
                    <div className="text-white font-bold text-lg">Sweet Treats Bakery</div>
                    <div className="text-slate-300 text-sm">{user.name || user.email}</div>
                    <div className={`text-xs font-bold mt-1 ${
                      isAdmin
                        ? 'text-red-300'
                        : currentTier === 'business' 
                        ? 'text-emerald-300'
                        : currentTier === 'professional'
                        ? 'text-blue-300'
                        : 'text-slate-400'
                    }`}>
                      {isAdmin ? '👑 Admin Access' : `${currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan`}
                    </div>
                  </div>
                </div>

                {/* Enhanced Mobile Navigation Items */}
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = isActivePath(item.path);
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-300 ${
                        isActive
                          ? item.isAdmin
                            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg shadow-red-500/25'
                            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/50 border border-slate-700/30 hover:border-slate-600/50'
                      }`}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <div className={`p-2 rounded-lg ${
                        isActive 
                          ? 'bg-white/20' 
                          : 'bg-slate-700/50'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-base flex items-center">
                          {item.label}
                          {item.isAdmin && (
                            <span className="ml-2 text-xs bg-red-500/30 px-2 py-1 rounded-full border border-red-400/30">
                              ADMIN
                            </span>
                          )}
                        </div>
                        <div className="text-xs opacity-80 mt-1">{item.description}</div>
                      </div>
                    </Link>
                  );
                })}

                {/* Enhanced Mobile Sign Out */}
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-4 px-5 py-4 text-slate-300 hover:text-white hover:bg-red-500/20 rounded-xl transition-all duration-300 mt-6 border-t border-slate-700/50 pt-6 border border-red-500/20 hover:border-red-500/40"
                  style={{ pointerEvents: 'auto' }}
                >
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <LogOut className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-base">Sign Out Securely</div>
                    <div className="text-xs opacity-80 mt-1">End your session safely</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Custom CSS for additional animations */}
      <style>{`        
      @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-2px); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 2s infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </>
  );
}